import { withServerProps } from '@web/library/with-server-props';
import { useTranslation } from 'react-i18next';
import {
  IndexListingsScreen,
  IndexListingsScreenProps,
} from 'shared/screens/listings/index-listings';
import { Page } from '@web/components/page';
import { PageBuilder } from 'shared/library/page-builder';
import { Breed } from 'shared/@types';
import { getListingsFilterQuery, ListingsFilter } from 'shared/state/listings-filter';
import { cities, City } from 'shared/library/cities';
import { useRoute } from 'shared/library/routes/route';
import { useRouter } from 'shared/library/use-router';
import { compactBreedForListingsPage } from '@web/library/compact-breed';
import { loadBreedListingsPageData } from 'shared/library/breed-listings-page-data';
import type { FallbackListingsKind } from 'shared/library/breed-listings-page-data';
import { getCanonicalBreedPageRedirect } from 'shared/library/canonical-breed-page';

interface IndexBreedCityListingsScreenProps extends IndexListingsScreenProps {
  lockedBreed: Breed;
  lockedCity: City;
  cityRadiusInKm: number;
  fallbackListingsKind: FallbackListingsKind | null;
}

export default function IndexBreedCityListings(props: IndexBreedCityListingsScreenProps) {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const router = useRouter();

  const title = t('listings.index_listings.locked_city_breed_title', {
    breed: props.lockedBreed!.name,
    city: props.lockedCity!.name,
  });

  const description = t('listings.index_listings.locked_city_breed_description', {
    breed: props.lockedBreed!.name,
    city: props.lockedCity!.name,
  });

  const fallbackTitle = props.fallbackListingsKind
    ? t(
        props.fallbackListingsKind === 'similar-breeds'
          ? 'listings.index_listings.similar_breeds_near_city_title'
          : 'listings.index_listings.other_animals_near_city_title',
        {
          city: props.lockedCity.name,
          animals: t(`common:enums.animal.${props.lockedBreed.animal}`, {
            count: 2,
          }).toLocaleLowerCase(),
        },
      )
    : undefined;
  const queryPathname = route('breeds.show.listings.by-city', {
    breed: props.lockedBreed.slug,
    city: props.lockedCity.slug,
  });
  const queryDefaults: Partial<ListingsFilter> = {
    breeds: [props.lockedBreed.id],
    latitude: props.lockedCity.latitude,
    longitude: props.lockedCity.longitude,
    country: props.lockedCity.country,
    maxDistanceInKm: props.cityRadiusInKm,
  };
  const shouldNoIndex =
    Object.keys(getListingsFilterQuery(props.initialFilter, queryDefaults)).some(
      (key) => key !== 'page',
    ) || props.initialListings.total === 0;

  function currentFilterQuery(
    currentFilter: ListingsFilter | undefined,
    defaults: Partial<ListingsFilter> = queryDefaults,
  ) {
    return getListingsFilterQuery(
      { ...(currentFilter ?? props.initialFilter), breeds: null, page: 1 },
      defaults,
    );
  }

  return (
    <Page
      {...props}
      register={(seo) => {
        const pageSeo = seo
          .setTitle(title)
          .setDescription(description)
          .setPagination(props.initialListings);

        return shouldNoIndex ? pageSeo.setNoIndex() : pageSeo;
      }}
    >
      <IndexListingsScreen
        {...props}
        title={title}
        subtitle={description}
        queryPathname={queryPathname}
        queryDefaults={queryDefaults}
        noResultsTitle={t('listings.index_listings.no_breed_near_city_title', {
          breed: props.lockedBreed.name,
          city: props.lockedCity.name,
        })}
        noResultsSubtitle={t('listings.index_listings.no_breed_near_city_description')}
        fallbackTitle={fallbackTitle}
        fallbackSubtitle={t('listings.index_listings.fallback_near_city_description', {
          city: props.lockedCity.name,
          radius: props.cityRadiusInKm,
        })}
        onSelectBreed={(breed, currentFilter) =>
          router.push(
            route(
              'breeds.show.listings.by-city',
              {
                breed: breed.slug,
                city: props.lockedCity.slug,
              },
              { query: currentFilterQuery(currentFilter) },
            ),
          )
        }
        onSelectBreedQuery={(query, currentFilter) =>
          router.push(
            route('listings.index', undefined, {
              query: {
                ...currentFilterQuery(currentFilter, {
                  breeds: [props.lockedBreed.id],
                }),
                breeds: query,
              },
            }),
          )
        }
        onClearBreedSelection={(currentFilter) =>
          router.push(
            route('listings.index', undefined, {
              query: currentFilter
                ? currentFilterQuery(
                    { ...currentFilter, breeds: null },
                    { breeds: [props.lockedBreed.id] },
                  )
                : {
                    latitude: props.lockedCity.latitude,
                    longitude: props.lockedCity.longitude,
                    country: props.lockedCity.country,
                    maxDistanceInKm: props.cityRadiusInKm,
                  },
            }),
          )
        }
        initialBreeds={[props.lockedBreed]}
      />
    </Page>
  );
}

const pageBuilder = new PageBuilder<
  IndexBreedCityListingsScreenProps,
  ListingsFilter & { city: string; breed: string; cityData?: City }
>()
  .prepareParams((params) => {
    const city = cities.find((c: City) => c.slug === params.city);

    if (!city) {
      return { notFound: true };
    }

    return {
      ...params,
      cityData: city,
    };
  })
  .request(async ({ req, params }) => ({
    ...(await loadBreedListingsPageData(req, params, params.cityData)),
    lockedCity: params.cityData!,
  }))
  .transformProps(({ props, params, ctx }) => {
    const redirect = getCanonicalBreedPageRedirect({
      breed: props.lockedBreed,
      requestedSlug: params.breed,
      locale: ctx.locale,
      hasWebResponse: Boolean(ctx.res),
      page: { name: 'breeds.show.listings.by-city', city: props.lockedCity.slug },
    });

    if (redirect) {
      return redirect;
    }

    return {
      ...props,
      lockedBreed: compactBreedForListingsPage(props.lockedBreed),
      initialFilter: {
        ...props.initialFilter,
        breeds: [props.lockedBreed.id],
      },
    };
  })
  .deriveRouteParams((props) => ({
    breed: props.lockedBreed.slug,
    city: props.lockedCity.slug,
  }));

export const getServerSideProps = withServerProps(pageBuilder);
